// ui/ExpandableText.js
import React, { useMemo, useState, useCallback } from "react";

/**
 * This function shows the note body content with a character count limit.
 * Offers option to expand or collapse the content (less/more toggle)
 * To avoid recalculating the shown characters, we use memoization (useMemo)
 *  It will be calculated if any of these change:
 *      The text being rendered
 *      expanded state - whether the note is expanded or collapsed
 *      limit - the amount of characters to show when collapsed
 *      needsClamp - boolean calculated based off the length of the text.
 */
export default function ExpandableText({ text = "", limit = 260 }) {
  const [expanded, setExpanded] = useState(false);
  const needsClamp = text?.length > limit;
  const shown = useMemo(
    () => (expanded || !needsClamp ? text : text.slice(0, limit).trimEnd()),
    [text, expanded, limit, needsClamp]
  );
  const toggle = useCallback(() => setExpanded((v) => !v), []);

  return (
    <div className="text-sm leading-6 text-text-secondary whitespace-pre-line">
      {shown}
      {needsClamp && !expanded && <span>…</span>}
      {needsClamp && (
        <button
          type="button"
          className="ml-1 text-sm font-medium text-text-muted hover:text-text-muted focus:outline-none"
          onClick={toggle}
          aria-expanded={expanded}
        >
          Read {expanded ? "less" : "more"}
        </button>
      )}
    </div>
  );
}
